import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Monitor, CalendarDays, FolderKanban, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const WORKSPACE_ICONS = {
  web: Monitor,
  event_center: CalendarDays,
  projects: FolderKanban,
};

const WORKSPACE_COLORS = {
  web: { text: "text-[#0099CC]", bg: "bg-[#0099CC]/10", border: "border-[#0099CC]/30" },
  event_center: { text: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]/30" },
  projects: { text: "text-[#10B981]", bg: "bg-[#10B981]/10", border: "border-[#10B981]/30" },
};

const WORKSPACE_LABELS = {
  web: "GLOTECH Web",
  event_center: "GLOTECH Event Center",
  projects: "GLOTECH Projects",
};

export default function WorkspaceSelector() {
  const { workspaces, activeWorkspace, setActiveWorkspace } = useAuth();
  const navigate = useNavigate();

  if (!workspaces || workspaces.length <= 1) return null;

  const currentWs = workspaces.find((w) => w.key === activeWorkspace) || workspaces[0];
  const Icon = WORKSPACE_ICONS[currentWs.key] || Monitor;
  const colors = WORKSPACE_COLORS[currentWs.key] || WORKSPACE_COLORS.web;

  const handleSwitch = (ws) => {
    if (ws.key === activeWorkspace) return;
    setActiveWorkspace(ws.key);
    navigate("/app", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${colors.bg} ${colors.border} border transition-all hover:opacity-80 cursor-pointer`}
          data-testid="workspace-selector-trigger"
        >
          <Icon className={`h-4 w-4 ${colors.text}`} />
          <span className={`text-sm font-semibold ${colors.text} hidden sm:inline`}>
            {currentWs.label || WORKSPACE_LABELS[currentWs.key]}
          </span>
          <ChevronDown className={`h-3.5 w-3.5 ${colors.text}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 bg-[#151B2B] border-[#2A3441] p-1"
        data-testid="workspace-selector-menu"
      >
        <div className="px-3 py-2 mb-1">
          <p className="text-[#64748b] text-xs uppercase tracking-wider font-mono">
            Changer d'espace
          </p>
        </div>
        {workspaces.map((ws) => {
          const WsIcon = WORKSPACE_ICONS[ws.key] || Monitor;
          const wsColors = WORKSPACE_COLORS[ws.key] || WORKSPACE_COLORS.web;
          const isActive = ws.key === activeWorkspace;

          return (
            <DropdownMenuItem
              key={ws.key}
              onClick={() => handleSwitch(ws)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                isActive
                  ? `${wsColors.bg} ${wsColors.border} border`
                  : "hover:bg-[#0B0F19] border border-transparent"
              }`}
              data-testid={`workspace-option-${ws.key}`}
            >
              <div className={`p-1.5 rounded-lg ${wsColors.bg}`}>
                <WsIcon className={`h-4 w-4 ${wsColors.text}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isActive ? "text-white" : "text-[#94A3B8]"}`}>
                  {WORKSPACE_LABELS[ws.key] || ws.label}
                </p>
                {ws.org_name && (
                  <p className="text-xs text-[#64748b] truncate">{ws.org_name}</p>
                )}
              </div>
              {isActive && <Check className={`h-4 w-4 ${wsColors.text} shrink-0`} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
